import { FlaskConical, Scale, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api, ApiError } from '../../api/client';
import type { AnalysisVariable, Weighting } from '../../api/types';
import { formatNumber } from '../../format';
import { confirmAction, Field, Modal, useToast } from '../ui';
import { useI18n } from '../../i18n';

/** Generate and remove test responses; set up weighting. Shown to questionnaire editors. */
export function AnalysisTools({
  surveyId,
  published,
  testTotal,
  weighting,
  variables,
  onChanged,
}: {
  surveyId: string;
  published: boolean;
  testTotal: number;
  weighting?: Weighting | null;
  variables: AnalysisVariable[];
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [count, setCount] = useState(50);
  const [busy, setBusy] = useState(false);
  const [weightOpen, setWeightOpen] = useState(false);
  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      toast(await fn());
      onChanged();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  };
  const label = variables.find((v) => v.key === weighting?.variable)?.label;
  return (
    <div className="grid cols-2 mt-16 no-print">
      <div className="card">
        <div className="card-head">
          <div className="tile">
            <FlaskConical />
          </div>
          <div className="grow">
            <h2>{t('tools.test_title')}</h2>
            <div className="sub">{t('tools.test_sub')}</div>
          </div>
        </div>
        <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
          <div className="row gap-8 wrap">
            <input className="input" type="number" min={1} max={500} style={{ width: 100 }} value={count} aria-label={t('tools.test_count')} onChange={(e) => setCount(Math.max(1, Math.min(500, Number(e.target.value) || 1)))} />
            <button
              className="btn"
              disabled={busy || !published}
              onClick={() => void run(async () => t('tools.test_created', { count: (await api.createTestResponses(surveyId, count)).created }))}
            >
              <FlaskConical /> {t('tools.test_generate')}
            </button>
            {testTotal > 0 && (
              <button
                className="btn ghost danger"
                disabled={busy}
                onClick={() => confirmAction(t('tools.test_confirm_delete', { count: testTotal })) && void run(async () => t('tools.test_deleted', { count: (await api.deleteTestResponses(surveyId)).deleted }))}
              >
                <Trash2 /> {t('tools.test_delete', { count: testTotal })}
              </button>
            )}
          </div>
          <p className="hint">{published ? t('tools.test_hint') : t('tools.test_publish_first')}</p>
        </div>
      </div>
      <div className="card">
        <div className="card-head">
          <div className="tile">
            <Scale />
          </div>
          <div className="grow">
            <h2>{t('tools.weight_title')}</h2>
            <div className="sub">{weighting ? t('tools.weight_by', { name: label ?? weighting.variable }) : t('tools.weight_off')}</div>
          </div>
          <button className="btn sm" onClick={() => setWeightOpen(true)}>
            {weighting ? t('tools.weight_edit') : t('tools.weight_setup')}
          </button>
        </div>
        <div className="card-pad" style={{ paddingTop: 0 }}>
          <p className="hint">{t('tools.weight_hint')}</p>
        </div>
      </div>
      {weightOpen && (
        <WeightingModal
          variables={variables.filter((v) => !v.multi && v.categories.length >= 2)}
          initial={weighting ?? undefined}
          onClose={() => setWeightOpen(false)}
          onSave={(w) =>
            void run(async () => {
              await api.saveAnalysisSettings(surveyId, { weighting: w });
              setWeightOpen(false);
              return w ? t('tools.weight_saved') : t('tools.weight_removed');
            })
          }
        />
      )}
    </div>
  );
}

function WeightingModal({ variables, initial, onClose, onSave }: { variables: AnalysisVariable[]; initial?: Weighting; onClose: () => void; onSave: (w: Weighting | null) => void }) {
  const { t } = useI18n();
  const [variable, setVariable] = useState(initial?.variable ?? variables[0]?.key ?? '');
  const [targets, setTargets] = useState<Record<string, number>>(initial?.targets ?? {});
  const v = variables.find((x) => x.key === variable);
  const sum = Object.entries(targets)
    .filter(([k]) => v?.categories.some((c) => c.id === k))
    .reduce((a, [, n]) => a + (n || 0), 0);
  const valid = !!v && sum > 0 && sum <= 100.5;
  return (
    <Modal
      title={t('tools.weight_title')}
      onClose={onClose}
      footer={
        <>
          {initial && (
            <button className="btn ghost danger" onClick={() => onSave(null)}>
              {t('tools.weight_remove')}
            </button>
          )}
          <span className="grow" />
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn primary"
            disabled={!valid}
            onClick={() => onSave({ variable, targets: Object.fromEntries(Object.entries(targets).filter(([k, n]) => n > 0 && v?.categories.some((c) => c.id === k))) })}
          >
            {t('common.save')}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('tools.weight_variable')} hint={t('tools.weight_variable_hint')}>
          <select
            className="select"
            value={variable}
            onChange={(e) => {
              setVariable(e.target.value);
              setTargets({});
            }}
          >
            {variables.map((x) => (
              <option key={x.key} value={x.key}>
                {x.label}
              </option>
            ))}
          </select>
        </Field>
        {v?.categories.map((c) => (
          <label key={c.id} className="sum-row">
            <span className="grow">{c.label}</span>
            <input
              className="input"
              type="number"
              min={0}
              max={100}
              step="any"
              value={targets[c.id] ?? ''}
              placeholder="0"
              onChange={(e) => setTargets((x) => ({ ...x, [c.id]: e.target.value === '' ? 0 : Number(e.target.value) }))}
            />
            <span className="muted">%</span>
          </label>
        ))}
        <div className={`sum-total ${Math.abs(sum - 100) < 0.5 ? 'ok' : sum > 100.5 ? 'over' : ''}`}>
          <span>{t('tools.weight_sum', { sum: formatNumber(Math.round(sum * 10) / 10) })}</span>
        </div>
        <p className="hint">{t('tools.weight_modal_hint')}</p>
      </div>
    </Modal>
  );
}
